import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import type { BinderyDamageType, BinderyRepairType } from "@/lib/api/bindery";
import { DAMAGE_TYPE_LABELS, REPAIR_TYPE_LABELS } from "../binderyFormatters";

export interface DraftItem {
  itemId: string;
  barcode: string;
  title: string;
  damageType?: BinderyDamageType;
  repairType?: BinderyRepairType;
  instructions?: string;
  cost?: number;
}

interface Props {
  item: DraftItem;
  onChange: (next: DraftItem) => void;
  onRemove: () => void;
}

export function BinderyItemLineEditor({ item, onChange, onRemove }: Props) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-mono text-sm">{item.barcode}</p>
          <p className="text-sm text-slate-600">{item.title}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <Label>Damage type</Label>
          <Select
            value={item.damageType ?? ""}
            onValueChange={(v) =>
              onChange({ ...item, damageType: (v as BinderyDamageType) || undefined })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select damage type" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DAMAGE_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Repair type</Label>
          <Select
            value={item.repairType ?? ""}
            onValueChange={(v) =>
              onChange({ ...item, repairType: (v as BinderyRepairType) || undefined })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select repair type" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(REPAIR_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Estimated cost</Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="0.00"
            value={item.cost ?? ""}
            onChange={(e) =>
              onChange({
                ...item,
                cost: e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
          />
        </div>
        <div className="md:col-span-2">
          <Label>Instructions</Label>
          <Textarea
            rows={2}
            value={item.instructions ?? ""}
            onChange={(e) => onChange({ ...item, instructions: e.target.value })}
            placeholder="Repair instructions for this item"
          />
        </div>
      </div>
    </div>
  );
}
